import React from "react";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { detailFieldSize } from "@/components/shared/DetailField";

/**
 * Lưới "Thông tin chung" phẳng: mỗi trường một ô, 4 cột (khung hẹp 2 cột), không bảng con, không thẻ màu.
 * Bố cục theo bề rộng khung chứa (form có thể nằm trong hộp thoại).
 */
export function QcInfoGrid({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className="@container min-w-0">
      <div className={cn("grid grid-cols-2 gap-x-3 gap-y-2.5 @3xl:grid-cols-4", className)}>{children}</div>
    </div>
  );
}

interface QcInfoFieldProps {
  label: React.ReactNode;
  htmlFor?: string;
  required?: boolean;
  /** Số cột chiếm: 1 (mặc định), 2, hoặc cả hàng */
  span?: 1 | 2 | "full";
  error?: string | null;
  children: React.ReactNode;
  className?: string;
}

export function QcInfoField({ label, htmlFor, required, span: spanProp, error, children, className }: QcInfoFieldProps) {
  // Không truyền span: rộng theo loại dữ liệu như màn chi tiết (lệnh SX, sản phẩm, NCC… 2 ô; ghi chú, lý do cả hàng)
  const span = spanProp ?? (typeof label === "string" ? ({ short: 1, medium: 2, full: "full" } as const)[detailFieldSize(label)] : 1);
  return (
    <div
      className={cn(
        "min-w-0 space-y-1",
        span === 2 && "col-span-2",
        span === "full" && "col-span-2 @3xl:col-span-4",
        className,
      )}
    >
      <Label htmlFor={htmlFor} className="text-xs font-semibold text-slate-700">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      {children}
      {error ? <p className="text-[11px] font-medium text-destructive">{error}</p> : null}
    </div>
  );
}

/** Giá trị chỉ đọc trong lưới thông tin chung (VD: Sản phẩm, Khách hàng lấy từ lệnh SX) */
export function QcInfoValue({ children, mono }: { children: React.ReactNode; mono?: boolean }) {
  return (
    <div
      className={cn(
        "flex min-h-9 min-w-0 items-center rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-semibold text-slate-800",
        mono && "font-mono",
      )}
    >
      {children === undefined || children === null || children === "" ? (
        <span className="text-slate-400">—</span>
      ) : (
        <span className="min-w-0 [overflow-wrap:anywhere]">{children}</span>
      )}
    </div>
  );
}
